// Card #3257 (scribe-fe resilience, AC2). A full or over-quota IndexedDB makes
// an outbox or scoreSheets write reject with a QuotaExceededError. Before this
// module nothing in scribe-fe looked for it: the rejection surfaced as an
// unhandled promise (scheduleSave's debounced persist), or was dropped by a
// `.catch(() => {})` on the local scoreSheets cache write, and the scribe kept
// scoring with no idea the edit had not reached disk.
//
// db.js reports every quota failure here (and still rethrows, so a caller's own
// error handling is unchanged); StorageFullBanner subscribes and tells the
// scribe, in plain language, for as long as the condition lasts. The condition
// self-clears the next time a guarded write succeeds, same self-clearing rule
// as the other alarms in this app (no dismiss button, kiosk doctrine).

let current = null;
const listeners = new Set();

function notify() {
  for (const listener of listeners) {
    try {
      listener(current);
    } catch {
      // A listener's own error is that listener's problem, never ours.
    }
  }
}

// Browsers disagree on the shape: Chrome/Firefox use name
// "QuotaExceededError", older Safari uses code 22, Firefox legacy used
// "NS_ERROR_DOM_QUOTA_REACHED" (code 1014), and idb can wrap the DOMException.
export function isQuotaError(err) {
  if (!err) return false;
  const name = err.name ?? err.cause?.name;
  if (name === "QuotaExceededError" || name === "NS_ERROR_DOM_QUOTA_REACHED") return true;
  return err.code === 22 || err.code === 1014;
}

export function reportStorageFailure(err, where = "write") {
  if (!isQuotaError(err)) return false;
  // A "reload" problem (below) outranks a full disk: keep showing it.
  if (current && current.kind !== "quota") return true;
  current = { kind: "quota", where, at: new Date().toISOString() };
  notify();
  return true;
}

// Card #3257 AC5 -- IndexedDB connection problems the scribe has to act on
// (db.js openDb). "blocked": THIS tab's open is waiting on an older tab that
// still holds the database (close the other tab, or reload). "stale": a newer
// bundle in another tab needs to upgrade the database, so this tab closed its
// connection; it can no longer read or write and must be reloaded. Both are
// shown by StorageFullBanner with a Reload button.
export function reportDbProblem(kind) {
  if (current?.kind === kind) return;
  current = { kind, at: new Date().toISOString() };
  notify();
}

export function clearDbProblem(kind) {
  if (current?.kind !== kind) return;
  current = null;
  notify();
}

// A guarded write that succeeded proves there is room again, but only for the
// same kind of write that failed: an unrelated tiny meta write succeeding must
// not hide the banner while the scribe's score writes are still being refused.
export function reportStorageWriteOk(where) {
  if (!current || current.where !== where) return;
  current = null;
  notify();
}

// Wrap one db write: quota failures are reported, then rethrown unchanged.
export async function guardWrite(where, fn) {
  try {
    const result = await fn();
    reportStorageWriteOk(where);
    return result;
  } catch (err) {
    reportStorageFailure(err, where);
    throw err;
  }
}

export function getStorageFailure() {
  return current;
}

export function subscribeStorageFailure(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function _resetStorageFailureForTests() {
  current = null;
  listeners.clear();
}
